import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  FileText, Send, RefreshCw, Calendar, Clock, Download,
  TrendingUp, Link2, Mail, CheckCircle2, BarChart3
} from 'lucide-react';

interface ReportData {
  generated_at: string;
  period: { start: string; end: string };
  keywords: {
    total: number;
    top10: number;
    top20: number;
    avgRanking: number;
    totalVolume: number;
  };
  backlinks: {
    total: number;
    secured: number;
    contacted: number;
    followUp: number;
    pending: number;
    newThisWeek: number;
  };
  emails: {
    totalSent: number;
    opened: number;
    clicked: number;
    openRate: number;
    clickRate: number;
  };
  topKeywords: Array<{
    keyword: string;
    ranking: number;
    volume: number;
    page: string;
  }>;
}

interface WeeklySEOReportProps {
  userId: string;
}

export const WeeklySEOReport = ({ userId }: WeeklySEOReportProps) => {
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleDay, setScheduleDay] = useState('monday');
  const [previousReports, setPreviousReports] = useState<any[]>([]);

  useEffect(() => {
    fetchPreviousReports();
  }, [userId]);

  const fetchPreviousReports = async () => {
    const { data, error } = await supabase
      .from('seo_reports')
      .select('*')
      .eq('report_type', 'weekly')
      .order('created_at', { ascending: false })
      .limit(5);

    if (!error && data) {
      setPreviousReports(data);
    }
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('weekly-seo-report', {
        body: { action: 'generate', user_id: userId }
      });

      if (error) throw error;

      setReportData(data.data);
      toast.success('Report generated successfully');
      fetchPreviousReports();
    } catch (error: any) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const sendReport = async () => {
    if (!emailTo) {
      toast.error('Please enter an email address');
      return;
    }

    setSending(true);
    try {
      const { data, error } = await supabase.functions.invoke('weekly-seo-report', {
        body: { action: 'send', email_to: emailTo, user_id: userId }
      });

      if (error) throw error;

      setReportData(data.data);
      toast.success(`Report sent to ${emailTo}`);
    } catch (error: any) {
      console.error('Error sending report:', error);
      toast.error('Failed to send report');
    } finally {
      setSending(false);
    }
  };

  const downloadReport = () => {
    if (!reportData) return;

    const content = `
WEEKLY SEO REPORT
Period: ${reportData.period.start} to ${reportData.period.end}
Generated: ${new Date(reportData.generated_at).toLocaleString()}

================================================================================
KEYWORD RANKINGS
================================================================================
Total Keywords: ${reportData.keywords.total}
Top 10 Rankings: ${reportData.keywords.top10}
Top 20 Rankings: ${reportData.keywords.top20}
Average Position: #${reportData.keywords.avgRanking}
Total Search Volume: ${reportData.keywords.totalVolume.toLocaleString()}

Top Keywords:
${reportData.topKeywords.map(k => `  - ${k.keyword}: #${k.ranking} (${k.volume?.toLocaleString() || 0} searches/mo)`).join('\n')}

================================================================================
BACKLINK PROGRESS
================================================================================
Total Targets: ${reportData.backlinks.total}
Secured: ${reportData.backlinks.secured}
Contacted: ${reportData.backlinks.contacted}
Follow-up Needed: ${reportData.backlinks.followUp}
Not Contacted: ${reportData.backlinks.pending}
New This Week: ${reportData.backlinks.newThisWeek}

================================================================================
EMAIL OUTREACH
================================================================================
Emails Sent: ${reportData.emails.totalSent}
Opened: ${reportData.emails.opened} (${reportData.emails.openRate}%)
Clicked: ${reportData.emails.clicked} (${reportData.emails.clickRate}%)
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `seo-report-${reportData.period.start}-to-${reportData.period.end}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report downloaded');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6" />
            Weekly SEO Report
          </h2>
          <p className="text-muted-foreground">Automated performance summaries</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={generateReport} disabled={loading}>
            {loading ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Generate Report
          </Button>
        </div>
      </div>

      {/* Send Report */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send Report
          </CardTitle>
          <CardDescription>Email the report to stakeholders</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <Label>Email Address</Label>
              <Input
                type="email"
                placeholder="team@company.com"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
              />
            </div>
            <Button onClick={sendReport} disabled={sending || !emailTo}>
              {sending ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send Report
            </Button>
          </div>

          <div className="mt-6 pt-6 border-t">
            <div className="flex items-center justify-between">
              <div>
                <Label>Automated Weekly Report</Label>
                <p className="text-sm text-muted-foreground">Automatically send reports every week</p>
              </div>
              <Switch
                checked={scheduleEnabled}
                onCheckedChange={setScheduleEnabled}
              />
            </div>
            {scheduleEnabled && (
              <div className="mt-4 flex gap-4">
                <div className="flex-1">
                  <Label>Send On</Label>
                  <Select value={scheduleDay} onValueChange={setScheduleDay}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monday">Monday</SelectItem>
                      <SelectItem value="tuesday">Tuesday</SelectItem>
                      <SelectItem value="wednesday">Wednesday</SelectItem>
                      <SelectItem value="thursday">Thursday</SelectItem>
                      <SelectItem value="friday">Friday</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Current Report */}
      {reportData && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              Report: {reportData.period.start} to {reportData.period.end}
            </h3>
            <Button variant="outline" onClick={downloadReport}>
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid md:grid-cols-3 gap-6">
            {/* Keywords */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Keyword Rankings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">{reportData.keywords.total}</div>
                    <div className="text-xs text-muted-foreground">Total</div>
                  </div>
                  <div className="text-center p-3 bg-green-500/10 rounded-lg">
                    <div className="text-2xl font-bold text-green-500">{reportData.keywords.top10}</div>
                    <div className="text-xs text-muted-foreground">Top 10</div>
                  </div>
                  <div className="text-center p-3 bg-blue-500/10 rounded-lg">
                    <div className="text-2xl font-bold text-blue-500">{reportData.keywords.top20}</div>
                    <div className="text-xs text-muted-foreground">Top 20</div>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">#{reportData.keywords.avgRanking}</div>
                    <div className="text-xs text-muted-foreground">Avg. Pos</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Backlinks */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Link2 className="h-5 w-5 text-primary" />
                  Backlink Progress
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">{reportData.backlinks.total}</div>
                    <div className="text-xs text-muted-foreground">Total</div>
                  </div>
                  <div className="text-center p-3 bg-green-500/10 rounded-lg">
                    <div className="text-2xl font-bold text-green-500">{reportData.backlinks.secured}</div>
                    <div className="text-xs text-muted-foreground">Secured</div>
                  </div>
                  <div className="text-center p-3 bg-blue-500/10 rounded-lg">
                    <div className="text-2xl font-bold text-blue-500">{reportData.backlinks.contacted}</div>
                    <div className="text-xs text-muted-foreground">Contacted</div>
                  </div>
                  <div className="text-center p-3 bg-primary/10 rounded-lg">
                    <div className="text-2xl font-bold text-primary">{reportData.backlinks.newThisWeek}</div>
                    <div className="text-xs text-muted-foreground">New</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email Outreach */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  Email Outreach
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">{reportData.emails.totalSent}</div>
                    <div className="text-xs text-muted-foreground">Sent</div>
                  </div>
                  <div className="text-center p-3 bg-blue-500/10 rounded-lg">
                    <div className="text-2xl font-bold text-blue-500">{reportData.emails.openRate}%</div>
                    <div className="text-xs text-muted-foreground">Open Rate</div>
                  </div>
                  <div className="text-center p-3 bg-green-500/10 rounded-lg">
                    <div className="text-2xl font-bold text-green-500">{reportData.emails.clickRate}%</div>
                    <div className="text-xs text-muted-foreground">Click Rate</div>
                  </div>
                  <div className="text-center p-3 bg-muted rounded-lg">
                    <div className="text-2xl font-bold">{reportData.emails.clicked}</div>
                    <div className="text-xs text-muted-foreground">Clicks</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Keywords Table */}
          {reportData.topKeywords.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Top Performing Keywords</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Keyword</TableHead>
                      <TableHead className="text-center">Ranking</TableHead>
                      <TableHead className="text-center">Volume</TableHead>
                      <TableHead>Page</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.topKeywords.map((keyword, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{keyword.keyword}</TableCell>
                        <TableCell className="text-center">
                          <Badge className={keyword.ranking <= 3 ? 'bg-green-500' : keyword.ranking <= 10 ? 'bg-blue-500' : ''}>
                            #{keyword.ranking}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {keyword.volume?.toLocaleString() || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground truncate max-w-[200px]">
                          {keyword.page}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Previous Reports */}
      {previousReports.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Previous Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {previousReports.map((report) => (
                <div key={report.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <div>
                      <p className="font-medium">{new Date(report.created_at).toLocaleDateString()}</p>
                      <p className="text-sm text-muted-foreground">{report.summary}</p>
                    </div>
                  </div>
                  <Badge variant="outline">
                    <Clock className="h-3 w-3 mr-1" />
                    {report.report_type}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
